/* eslint-disable */
const LARGE_COUNTRY_CITIES = {
  IN: ["Mumbai", "Delhi", "Bangalore", "Chennai", "Hyderabad", "Pune", "Kolkata", "Ahmedabad"],
  US: ["New York", "Los Angeles", "Chicago", "Houston", "Dallas", "Miami", "Seattle", "Phoenix"],
  AU: ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide"],
  CA: ["Toronto", "Vancouver", "Montreal", "Calgary"],
  GB: ["London", "Manchester", "Birmingham", "Leeds", "Glasgow"],
  DE: ["Berlin", "Munich", "Hamburg", "Frankfurt", "Cologne"],
};

const NON_COMMERCIAL_NAME_WORDS = [
  // --- Government / Public sector (English + all supported countries) ---
  "municipality", "municipal", "ministry", "ministere", "ministerium", "ministerio",
  "department", "directorate", "council", "commune", "gemeinde", "gemeente",
  "kommune", "fylkeskommune", "statsforvaltning", "fylke",       // NO
  "stad", "stad", "landsting", "region", "regionen",             // SE/DK
  "bundesamt", "landesamt", "bezirk", "amt",                     // DE
  "panchayat", "nagar palika", "gram sabha", "mandal",           // IN
  "baladiya", "majlis", "waqf", "amana",                         // AE/SA
  "municipality of", "city of", "district of", "shire of",       // AU/CA/ZA
  "county", "borough", "parish", "prefecture",
  "government", "authority", "agency", "public sector",
  // --- Education ---
  "school", "skole", "skola", "schule", "école", "scuola", "escuela",
  "university", "universitet", "universität", "universiteit",
  "college", "academy", "akademi", "akademie", "institut",
  "institute", "polytechnic", "seminary", "madrasah", "madrasa",
  // --- Religious ---
  "church", "kirke", "kirche", "kerk", "chiesa", "iglesia",
  "mosque", "masjid", "temple", "mandir", "cathedral", "chapel",
  "synagogue", "gurudwara", "vihara", "diocese", "parish church",
  // --- Culture / Public amenity ---
  "library", "bibliotek", "bibliothek", "bibliotheek",
  "museum", "theatre", "theater", "teater", "opera", "gallery",
  "archive", "monument", "historic site",
  // --- Charities / Civil society ---
  "foundation", "stiftelse", "stiftung", "stichting", "fundación",
  "charity", "charitable", "ngo", "non-governmental", "nonprofit",
  "not for profit", "not-for-profit", "benevolent", "humanitarian",
  "association", "forening", "förening", "verein", "verband",
  "société", "unie", "union", "federation", "konfederasjon",
  "idrettslag", "sports club", "sportclub", "sporting club",
  "society", "fraternity", "sorority", "alumni",
  // --- Healthcare / Emergency ---
  "hospital", "sykehus", "sjukhus", "krankenhaus", "ziekenhuis",
  "clinic", "dispensary", "public health", "health centre",
  "police", "politiet", "polizei", "politie", "carabinieri",
  "fire service", "fire department", "fire brigade",
  // --- Other non-commercial labels ---
  "trust", "endowment", "cooperative society", "residents association",
  "homeowners association", "parent teacher", "burial society",
];

const COMMERCIAL_HINT_WORDS = [
  // Scandinavia
  "as", "asa", "da", "enk", "ans", "iks",
  // UK / Ireland
  "ltd", "limited", "plc", "llp",
  // USA / Canada
  "llc", "inc", "corp", "incorporated",
  // International / generic
  "company", "co", "group",
  // Germany / Austria / CH
  "gmbh", "ag", "kg", "ohg", "ug",
  // France / Belgium
  "sarl", "sas", "snc", "sa",
  // Netherlands
  "bv", "nv",
  // Denmark
  "aps", "is",
  // Finland
  "oy", "oyj",
  // Sweden
  "ab",
  // Singapore / Malaysia
  "pte", "sdn", "bhd",
  // South Africa
  "pty",
  // India
  "pvt",
  // Australia
  "pty ltd",
  // Other European
  "kft", "rt", "sro", "srl", "sprl",
];

const HARD_BLOCK_NAME_PHRASES = [
  "small business administration", "u.s. small business administration", " sba ",
  "city of ", "county of ", "state of ", "department of ", "ministry of ",
  "office of ", "bureau of ", "public works", "housing authority",
  "economic development authority", "business advisory board",
];

const LOW_CONTACT_INDUSTRY_PHRASES = [
  "government office", "public administration", "municipal administration",
  "civic administration", "city government", "county government",
  "state government", "federal government", "public sector",
];

const LOW_CONTACT_DOMAIN_SUFFIXES = [".gov", ".edu", ".mil", ".int"];

function hasWord(text, word) {
  const normalized = ` ${String(text || "").toLowerCase()} `;
  return normalized.includes(` ${word} `);
}

function isLikelyNonCommercial(lead) {
  const name = String(lead?.name || "").toLowerCase();
  const industry = String(lead?.industry || "").toLowerCase();
  const combined = `${name} ${industry}`;

  const hasCommercialHint = COMMERCIAL_HINT_WORDS.some((word) => hasWord(name, word) || combined.includes(word + "."));
  const hasNonCommercialHint = NON_COMMERCIAL_NAME_WORDS.some((word) => combined.includes(word));
  const hasNoBusinessSignals = !lead?.website && !lead?.email && !lead?.phone && Number(lead?.employees || 0) === 0;

  return hasNonCommercialHint && !hasCommercialHint && hasNoBusinessSignals;
}

function normalizeText(value) {
  return ` ${String(value || "").toLowerCase()} `;
}

function hasAnyPhrase(text, phrases) {
  const normalized = normalizeText(text);
  return phrases.some((phrase) => normalized.includes(phrase));
}

function hasLowContactDomain(website) {
  if (!website) return false;
  try {
    const url = String(website).startsWith("http") ? String(website) : `https://${website}`;
    const hostname = new URL(url).hostname.toLowerCase();
    return LOW_CONTACT_DOMAIN_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
  } catch {
    return false;
  }
}

function isLowContactabilityLead(lead) {
  const name = String(lead?.name || "").toLowerCase();
  const industry = String(lead?.industry || "").toLowerCase();
  const source = String(lead?.source || "").toLowerCase();
  const combined = `${name} ${industry} ${source}`;

  const hasCommercialHint = COMMERCIAL_HINT_WORDS.some((word) => hasWord(name, word) || combined.includes(word + "."));
  const hardBlockedByName = hasAnyPhrase(combined, HARD_BLOCK_NAME_PHRASES);
  const blockedByIndustry = hasAnyPhrase(industry, LOW_CONTACT_INDUSTRY_PHRASES);
  const blockedByDomain = hasLowContactDomain(lead?.website);

  // Strong filter: remove obvious public/institutional entities even if they have a website.
  if (hardBlockedByName || blockedByIndustry || blockedByDomain) {
    return !hasCommercialHint;
  }

  return false;
}

// Patterns that identify article/blog titles masquerading as company names
const ARTICLE_TITLE_PATTERNS = [
  /^\d+\s+\w+\s+(to|for|of|that|you|a|an)\b/i,            // "4 ways to", "5 tips for"
  /^how\s+(to\s+|[a-z]+\s+you)/i,                          // "How to", "How companies can"
  /^(why|when|where)\s+(you|your|companies?|businesses?|organizations?|the\s)/i,
  /^(the\s+)?(future|power|art|science|guide|key|role|impact|benefits?|challenges?|importance|evolution|state|rise|fall)\s+of\s+/i,
  /^(a\s+)?(guide|introduction|overview|look|deep.?dive)\s+(to|at|into|on)\s+/i,
  /^what\s+(is\s+|are\s+|you\s+|every\s+)/i,
  /^(understanding|exploring|navigating|building|breaking\s+down|rethinking|transforming|leveraging|unlocking)\s+/i,
  /^(top\s+\d+|best\s+\d+|\d+\s+best|\d+\s+top)\b/i,
  /^(new|study|report|survey|research):/i,                  // "Report: companies must..."
];

function isArticleTitleLead(lead) {
  const source = String(lead?.source || "").toLowerCase();
  if (!source.includes("opportunit") && !source.includes("public")) return false;

  const name = String(lead?.name || "").trim();
  if (!name) return false;

  // Very long names are almost certainly article headlines
  if (name.length > 75) return true;

  // Article-style colon title format with 5+ words (e.g. "Procurement at a Crossroads: Evolve or...")
  if (name.includes(": ") && name.split(/\s+/).length > 5) return true;

  return ARTICLE_TITLE_PATTERNS.some((pattern) => pattern.test(name));
}

export function filterCommercialLeads(leads = []) {
  return leads.filter((lead) => !isLikelyNonCommercial(lead) && !isLowContactabilityLead(lead) && !isArticleTitleLead(lead));
}

export async function searchNorway({ api, q, industry, city }) {
  const searchTerm = q || (industry !== "All Industries" ? industry : city !== "All Cities" ? city : "oslo");
  const res = await fetch(`${api}/api/proxy/norway?q=${encodeURIComponent(searchTerm)}`);
  if (!res.ok) throw new Error("Norway proxy error: " + res.status);
  const data = await res.json();
  return (data?._embedded?.enheter || []).map((entity) => ({
    id: `NO-${entity.organisasjonsnummer}`,
    name: entity.navn,
    country: "Norway",
    source: "Norway",
    industry: entity.naeringskode1?.beskrivelse || "",
    city: entity.forretningsadresse?.poststed || "",
    address: [entity.forretningsadresse?.adresse?.[0], entity.forretningsadresse?.poststed].filter(Boolean).join(", "),
    employees: entity.antallAnsatte || 0,
    website: null,
    websiteStatus: "unknown",
    phone: null,
    orgNumber: entity.organisasjonsnummer,
  }));
}

export async function searchUK({ api, q, city, industry }) {
  const params = new URLSearchParams({
    q: q || "ltd",
    city: city !== "All Cities" ? city : "",
    industry: industry !== "All Industries" ? industry : "",
  });
  const res = await fetch(`${api}/api/proxy/uk?${params}`);
  if (!res.ok) throw new Error("UK proxy error: " + res.status);
  const data = await res.json();
  return (data?.items || []).map((entity) => ({
    id: `GB-${entity.company_number}`,
    name: entity.title,
    country: "United Kingdom",
    source: "United Kingdom",
    industry: entity.description || "",
    city: entity.address?.locality || entity.address?.postal_code || "",
    address: [entity.address?.address_line_1, entity.address?.locality, entity.address?.postal_code].filter(Boolean).join(", "),
    employees: 0,
    website: null,
    websiteStatus: "unknown",
    phone: null,
    orgNumber: entity.company_number,
  }));
}

export async function searchDenmark({ api, q }) {
  const res = await fetch(`${api}/api/proxy/denmark?search=${encodeURIComponent(q || "aps")}`);
  const data = await res.json();
  const arr = Array.isArray(data) ? data : (data?.vat ? [data] : []);
  return arr.map((entity) => ({
    id: `DK-${entity.vat}`,
    name: entity.name,
    country: "Denmark",
    source: "Denmark",
    industry: entity.industrycode_text || "",
    city: entity.city || "",
    address: entity.address || "",
    employees: entity.employees || 0,
    website: entity.webpage ? (entity.webpage.startsWith("http") ? entity.webpage : "https://" + entity.webpage) : null,
    websiteStatus: entity.webpage ? "found" : "unknown",
    phone: entity.phone || null,
    orgNumber: entity.vat,
  }));
}

async function fetchMapsPage({ api, q, countryId, pageToken = "" }) {
  const url = `${api}/api/proxy/maps?q=${encodeURIComponent(q)}&country=${countryId}&cc=${countryId}${pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ""}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Maps error: " + res.status);
  return res.json();
}

export async function searchMaps({ api, q, countryId, sources }) {
  const src = sources.find((source) => source.id === countryId);
  const bannedTypes = new Set(["country", "administrative_area_level_1", "administrative_area_level_2", "locality", "postal_code", "political"]);
  const seenIds = new Set();
  const allPlaces = [];

  const toLeads = (places) => places
    .filter((place) => place.placeId && !seenIds.has(place.placeId) && !bannedTypes.has((place.primaryType || "").toLowerCase()))
    .map((place) => {
      seenIds.add(place.placeId);
      return {
        id: `${countryId}-${place.placeId}`,
        name: place.name,
        country: src?.label || countryId,
        source: src?.label || countryId,
        industry: place.primaryType?.replace(/_/g, " ") || "",
        city: place.city || "",
        address: place.address || "",
        employees: 0,
        website: place.website || null,
        websiteStatus: place.website ? "found" : "unknown",
        phone: place.phone || null,
        rating: place.rating,
        ratingCount: place.userRatingCount,
      };
    });

  let pageToken = "";
  for (let page = 0; page < 3; page++) {
    try {
      const data = await fetchMapsPage({ api, q, countryId, pageToken });
      const raw = Array.isArray(data) ? data : (data.places || []);
      allPlaces.push(...toLeads(raw));
      pageToken = data.nextPageToken || "";
      if (!pageToken) break;
      await new Promise((resolve) => setTimeout(resolve, 1500));
    } catch {
      break;
    }
  }

  const cities = LARGE_COUNTRY_CITIES[countryId];
  if (cities && allPlaces.length < 40) {
    const cityQueries = cities.slice(0, 4).map((city) => {
      const cityQ = q.toLowerCase().includes(city.toLowerCase()) ? q : `${q} in ${city}`;
      return fetchMapsPage({ api, q: cityQ, countryId })
        .then((data) => toLeads(Array.isArray(data) ? data : (data.places || [])))
        .catch(() => []);
    });
    const cityResults = await Promise.all(cityQueries);
    allPlaces.push(...cityResults.flat());
  }

  return allPlaces;
}

export async function searchPublicOpportunities({ api, q, countryId, limit, industry, city, sources, inferWebsiteFromOpportunityItem, inferCountryFromOpportunityItem }) {
  const countrySource = sources.find((source) => source.id === countryId);
  const cc = countryId === "GLOBAL" ? "" : (countrySource ? countryId : "");
  const res = await fetch(`${api}/api/proxy/opportunities?q=${encodeURIComponent(q)}&cc=${cc}&limit=${limit}`);
  if (!res.ok) throw new Error("Public opportunities error: " + res.status);
  const data = await res.json();
  const items = Array.isArray(data.items) ? data.items : [];

  return items.map((item, index) => {
    const inferredWebsite = inferWebsiteFromOpportunityItem(item);
    const inferredCountry = countryId === "GLOBAL"
      ? inferCountryFromOpportunityItem(item, "Global")
      : (countrySource?.label || "Global");
    const sourceType = item.source ? `${item.source} Public Feed` : "Public Opportunities Feed";
    return {
      id: `OPP-${countryId}-${Date.now()}-${index}`,
      name: item.title,
      country: inferredCountry,
      source: "Public Opportunities",
      sourceType,
      sourceUrl: item.link,
      postedAt: item.pubDate || null,
      legalBasis: "Publicly available source",
      industry: industry !== "All Industries" ? industry : "Opportunity",
      city: city !== "All Cities" ? city : "",
      address: "",
      employees: 0,
      website: inferredWebsite,
      websiteStatus: inferredWebsite ? "found" : "unknown",
      phone: null,
      description: item.description || "",
    };
  });
}

export async function autoPingWebsite({ api, lead }) {
  if (lead.website) return lead;
  const raw = (lead.name || "")
    .toLowerCase()
    .replace(/\s+(as|da|ab|asa|inc|llc|ltd|gmbh|sa|oslo|bergen|stavanger|trondheim|norway|norge|uk)\b/gi, "")
    .replace(/[^a-z0-9]/g, "");
  if (!raw || raw.length < 2) return lead;

  const tlds = lead.country === "Norway" ? [".no", ".com"]
    : lead.country === "United Kingdom" ? [".co.uk", ".com"]
    : lead.country === "Denmark" ? [".dk", ".com"]
    : [".com"];

  for (const tld of tlds) {
    const domain = raw + tld;
    try {
      const r = await fetch(`${api}/api/proxy/ping-website?url=${encodeURIComponent(domain)}`);
      const d = await r.json();
      if (d.ok) return { ...lead, website: "https://" + domain, websiteStatus: "found" };
    } catch {}
  }

  return { ...lead, websiteStatus: lead.websiteStatus || "unknown" };
}

export async function enrichLeadEmail({ api, lead, hasUsableWebsite, hunterKey }) {
  if (!hasUsableWebsite(lead) || lead.email) return lead;

  try {
    const crawlRes = await fetch(`${api}/api/proxy/crawl-email?url=${encodeURIComponent(lead.website)}`);
    const crawlData = await crawlRes.json();
    const crawledEmail = crawlData?.emails?.[0] || "";
    if (crawledEmail) return { ...lead, email: crawledEmail };
  } catch {}

  if (!hunterKey) return lead;
  try {
    const domain = new URL(lead.website.startsWith("http") ? lead.website : `https://${lead.website}`).hostname.replace("www.", "");
    const blockedDomains = ["news.google.com", "google.com", "linkedin.com", "facebook.com", "instagram.com", "x.com", "twitter.com", "youtube.com", "reddit.com", "bing.com"];
    if (blockedDomains.some((d) => domain === d || domain.endsWith(`.${d}`))) return lead;

    const hunterRes = await fetch(`${api}/api/proxy/hunter-domain?domain=${encodeURIComponent(domain)}`);
    if (!hunterRes.ok) return lead;
    const hunterData = await hunterRes.json();
    const hunterEmail = hunterData?.data?.emails?.[0]?.value || "";
    if (hunterEmail) return { ...lead, email: hunterEmail };
  } catch {}

  return lead;
}

export async function enrichLeadDirectContact({ api, lead, hasUsableWebsite, hunterKey }) {
  const hasDirectContact = !!(
    lead.email ||
    lead.phone ||
    lead.projectContactEmails?.[0] ||
    lead.projectContactPhones?.[0]
  );
  if (hasDirectContact || !hasUsableWebsite(lead)) return lead;

  let nextLead = lead;

  try {
    const res = await fetch(`${api}/api/proxy/crawl-project?url=${encodeURIComponent(lead.website)}`);
    const data = await res.json();
    if (res.ok && data?.ok) {
      const projectEmail = data.contact?.emails?.[0] || "";
      const projectPhone = data.contact?.phones?.[0] || "";
      nextLead = {
        ...nextLead,
        email: nextLead.email || projectEmail || null,
        phone: nextLead.phone || projectPhone || null,
        projectContactEmails: data.contact?.emails || nextLead.projectContactEmails || [],
        projectContactPhones: data.contact?.phones || nextLead.projectContactPhones || [],
      };
    }
  } catch {}

  if (nextLead.email || nextLead.phone || nextLead.projectContactEmails?.[0] || nextLead.projectContactPhones?.[0]) {
    return nextLead;
  }

  return enrichLeadEmail({ api, lead: nextLead, hasUsableWebsite, hunterKey });
}

export async function searchDoffin({ api, q, keyword }) {
  const term = encodeURIComponent(encodeURIComponent(q || keyword || "") || "IT");
  const res = await fetch(`${api}/api/proxy/doffin?q=${term}&limit=20`);
  if (!res.ok) throw new Error("Doffin error: " + res.status);
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Doffin error");
  return (data.leads || []).map((lead) => ({
    ...lead,
    websiteStatus: lead.website ? "found" : "unknown",
    status: "New",
    decision: "Undecided",
    skills: [],
    note: "",
  }));
}


export async function searchSAM({ api, q, keyword }) {
  const term = q || keyword || "website design";
  const res = await fetch(`${api}/api/proxy/sam?q=${encodeURIComponent(term)}&limit=20`);
  if (!res.ok) throw new Error("SAM.gov error: " + res.status);
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "SAM.gov error");
  return (data.leads || []).map((l) => ({
    ...l,
    websiteStatus: l.website ? "found" : "unknown",
    status: "New",
    decision: "Undecided",
    skills: [],
    note: "",
  }));
}

export async function searchContractsFinder({ api, q, keyword }) {
  const term = q || keyword || "digital";
  const res = await fetch(`${api}/api/proxy/contracts-finder?q=${encodeURIComponent(term)}&limit=20`);
  if (!res.ok) throw new Error("Contracts Finder error: " + res.status);
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "Contracts Finder error");
  return (data.leads || []).map((l) => ({
    ...l,
    websiteStatus: l.website ? "found" : "unknown",
    status: "New",
    decision: "Undecided",
    skills: [],
    note: "",
  }));
}

export async function searchTED({ api, q, keyword }) {
  const term = q || keyword || "digital services";
  const res = await fetch(`${api}/api/proxy/ted?q=${encodeURIComponent(term)}&limit=20`);
  if (!res.ok) throw new Error("TED error: " + res.status);
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "TED error");
  return (data.leads || []).map((l) => ({
    ...l,
    websiteStatus: l.website ? "found" : "unknown",
    status: "New",
    decision: "Undecided",
    skills: [],
    note: "",
  }));
}
